/**
 * Bernard Miniatures — always-open search bar.
 *
 * Opens a panel on focus showing top brands, then swaps to live product and
 * collection results as the visitor types. Results come from Shopify's
 * predictive search endpoint rendered through sections/bm-search-results.liquid.
 */

const MIN_CHARS = 2;
const DEBOUNCE = 200;
const RECENT_KEY = 'bm:recent-searches';
const RECENT_MAX = 6;

class BmSearch extends HTMLElement {
  #timer = null;
  #fetch = null;
  #open = false;
  #lastTerm = '';
  #frame = null;

  connectedCallback() {
    this.input = this.querySelector('[data-bm-input]');
    this.panel = this.querySelector('[data-bm-panel]');
    this.resting = this.querySelector('[data-bm-resting]');
    this.results = this.querySelector('[data-bm-results]');
    this.scrim = this.querySelector('[data-bm-scrim]');
    this.clearButton = this.querySelector('[data-bm-clear]');
    this.status = this.querySelector('[data-bm-status]');
    this.form = this.querySelector('form');
    if (!this.input || !this.panel) return;

    this.limit = Number(this.dataset.limit) || 6;
    this.endpoint = window.Theme?.routes?.predictive_search_url || '/search/suggest';
    this.sectionId = this.dataset.sectionId || 'bm-search-results';
    this.i18nResults = this.dataset.i18nResults || '';
    this.i18nNoResults = this.dataset.i18nNoResults || '';

    this.#renderRecent();
    this.#measure();

    this.input.addEventListener('focus', () => this.openPanel());
    this.input.addEventListener('click', () => this.openPanel());
    this.input.addEventListener('input', this.#onInput);
    this.addEventListener('keydown', this.#onKeydown);
    this.clearButton?.addEventListener('click', this.#onClear);
    this.scrim?.addEventListener('click', () => this.closePanel());
    this.form?.addEventListener('submit', () => this.#remember(this.input.value));
    this.querySelector('[data-bm-recent-clear]')?.addEventListener('click', this.#forgetAll);

    this.onDocumentPointer = (event) => {
      if (!this.#open) return;
      if (this.contains(event.target)) return;
      this.closePanel();
    };
    document.addEventListener('pointerdown', this.onDocumentPointer);

    this.onScroll = () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#measure());
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });

    if (this.input.value.trim()) this.#toggleClear(true);
  }

  disconnectedCallback() {
    document.removeEventListener('pointerdown', this.onDocumentPointer);
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    clearTimeout(this.#timer);
    cancelAnimationFrame(this.#frame);
    this.#fetch?.abort();
  }

  /* ---------------------------------------------------------------- layout */

  /**
   * Locate the origin that `position: fixed` actually resolves against.
   *
   * Horizon puts `contain: layout` on the header, which makes it the containing
   * block for fixed descendants — so viewport coordinates land in the wrong
   * place. Rather than hard-code that, park the panel at 0,0 and read back
   * where it lands: that is the origin, whatever produced it.
   *
   * @returns {{top: number, left: number}} Origin in viewport coordinates.
   */
  #origin() {
    this.style.setProperty('--bm-s-top', '0px');
    this.style.setProperty('--bm-s-left', '0px');
    const probe = this.panel.getBoundingClientRect();
    return { top: probe.top, left: probe.left };
  }

  #measure() {
    // The panel hangs off the field on all three axes, so it stays attached to
    // the input wherever the merchant puts the bar in the header — above the
    // menu row, below it, or on a row of its own.
    const field = this.querySelector('.bm-search__field');
    if (!field || this.panel.hidden) return;

    const origin = this.#origin();
    const rect = field.getBoundingClientRect();
    const top = rect.bottom + 6;

    this.style.setProperty('--bm-s-top', `${Math.round(top - origin.top)}px`);
    this.style.setProperty('--bm-s-left', `${Math.round(rect.left - origin.left)}px`);
    this.style.setProperty('--bm-s-width', `${Math.round(rect.width)}px`);

    // The scrim is fixed too, so it needs the same correction to reach the
    // viewport edges instead of the header's.
    this.style.setProperty('--bm-s-scrim-left', `${Math.round(-origin.left)}px`);
    this.style.setProperty('--bm-s-scrim-height', `${Math.round(window.innerHeight - top)}px`);
  }

  /* ------------------------------------------------------------- open/close */

  openPanel() {
    if (this.#open) return;
    // Un-hide first: the panel has to be laid out for #measure to read where a
    // fixed element actually lands.
    this.panel.hidden = false;
    if (this.scrim) this.scrim.hidden = false;
    this.#measure();
    void this.panel.offsetHeight;
    this.classList.add('is-open');
    this.input.setAttribute('aria-expanded', 'true');
    this.#open = true;
  }

  closePanel() {
    if (!this.#open) return;
    this.classList.remove('is-open');
    this.input.setAttribute('aria-expanded', 'false');
    this.#open = false;
    this.#clearActive();
    setTimeout(() => {
      if (!this.classList.contains('is-open')) {
        this.panel.hidden = true;
        if (this.scrim) this.scrim.hidden = true;
      }
    }, 220);
  }

  /* ------------------------------------------------------------------ input */

  #onInput = () => {
    const term = this.input.value.trim();
    this.#toggleClear(term.length > 0);
    this.openPanel();

    clearTimeout(this.#timer);

    if (term.length < MIN_CHARS) {
      this.#showResting();
      return;
    }

    this.#timer = setTimeout(() => this.#search(term), DEBOUNCE);
  };

  #onClear = () => {
    this.input.value = '';
    this.#toggleClear(false);
    this.#showResting();
    this.input.focus();
  };

  #toggleClear(show) {
    if (this.clearButton) this.clearButton.hidden = !show;
  }

  #showResting() {
    this.#lastTerm = '';
    this.classList.remove('is-loading');
    if (this.resting) this.resting.hidden = false;
    if (this.results) {
      this.results.hidden = true;
      this.results.replaceChildren();
    }
    this.#clearActive();
  }

  /* ----------------------------------------------------------------- search */

  async #search(term) {
    if (term === this.#lastTerm) return;
    this.#lastTerm = term;

    this.#fetch?.abort();
    const controller = new AbortController();
    this.#fetch = controller;
    this.classList.add('is-loading');

    const url = new URL(this.endpoint, location.origin);
    url.searchParams.set('q', term);
    // Asking for 'query' as well makes Shopify return search suggestions
    // *instead of* products for a good number of terms — see the 2cv case.
    url.searchParams.set('resources[type]', 'product,collection');
    url.searchParams.set('resources[limit]', String(this.limit));
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', this.sectionId);

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const payload = doc.querySelector('bm-search-payload');
      if (!payload) throw new Error('payload absent');

      if (controller.signal.aborted) return;

      this.results.replaceChildren(...payload.childNodes);
      this.results.hidden = false;
      if (this.resting) this.resting.hidden = true;
      this.#announce(payload.dataset.total, term);
      this.#clearActive();
    } catch (error) {
      if (error.name === 'AbortError') return;
      // A failed suggestion must never block the visitor: fall back to the
      // resting panel, the form still submits to the full search page.
      this.#showResting();
    } finally {
      if (this.#fetch === controller) {
        this.classList.remove('is-loading');
        this.#fetch = null;
      }
    }
  }

  #announce(total, term) {
    if (!this.status) return;
    const count = Number(total) || 0;
    const template = count ? this.i18nResults : this.i18nNoResults;
    this.status.textContent = template.replace('%count%', count).replace('%query%', term);
  }

  /* --------------------------------------------------------------- keyboard */

  get #options() {
    return [...this.panel.querySelectorAll('a')].filter((el) => el.offsetParent !== null);
  }

  #clearActive() {
    this.panel.querySelectorAll('.is-active').forEach((el) => el.classList.remove('is-active'));
    this.input.removeAttribute('aria-activedescendant');
  }

  #onKeydown = (event) => {
    if (event.key === 'Escape') {
      if (this.#open) {
        event.stopPropagation();
        this.closePanel();
        this.input.focus();
      }
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    const options = this.#options;
    if (!options.length) return;
    event.preventDefault();
    this.openPanel();

    const current = options.findIndex((el) => el.classList.contains('is-active'));
    const step = event.key === 'ArrowDown' ? 1 : -1;
    const next = current === -1 ? (step === 1 ? 0 : options.length - 1) : (current + step + options.length) % options.length;

    this.#clearActive();
    const target = options[next];
    target.classList.add('is-active');
    target.scrollIntoView({ block: 'nearest' });
    if (!target.id) target.id = `bm-option-${next}`;
    this.input.setAttribute('aria-activedescendant', target.id);
  };

  /* ------------------------------------------------------- recent searches */

  #readRecent() {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter((item) => typeof item === 'string') : [];
    } catch {
      return [];
    }
  }

  #remember(value) {
    const term = String(value || '').trim();
    if (term.length < MIN_CHARS) return;
    try {
      const list = [term, ...this.#readRecent().filter((item) => item.toLowerCase() !== term.toLowerCase())];
      localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch {
      // Storage unavailable (private mode, quota): recents are a nicety, skip.
    }
  }

  #forgetAll = () => {
    try {
      localStorage.removeItem(RECENT_KEY);
    } catch {
      // Nothing to do.
    }
    this.#renderRecent();
  };

  #renderRecent() {
    const wrapper = this.querySelector('[data-bm-recent]');
    const list = this.querySelector('[data-bm-recent-list]');
    if (!wrapper || !list) return;

    const terms = this.#readRecent();
    if (!terms.length) {
      wrapper.hidden = true;
      list.replaceChildren();
      return;
    }

    const searchUrl = window.Theme?.routes?.search_url || '/search';
    list.replaceChildren(
      ...terms.map((term) => {
        const chip = document.createElement('a');
        chip.className = 'bm-search__chip';
        chip.href = `${searchUrl}?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
        chip.textContent = term;
        return chip;
      })
    );
    wrapper.hidden = false;
  }
}

if (!customElements.get('bm-search')) {
  customElements.define('bm-search', BmSearch);
}
